import {BackendError, Group, SearchFilter, SearchRequest} from "@/lib/types";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/components/Page";
import {dataRetryPolicy, fetchBackendURLParams} from "@/lib/backend";
import {useQuery} from "@tanstack/react-query";
import {useLocalSearchParams, useRouter} from "expo-router";
import Message from "@/lib/components/Message";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import {ActivityIndicator, Platform, Pressable, StyleSheet, TextInput, View} from "react-native";
import Icon from "@/lib/components/Icon";
import {Color} from "@/lib/styles";
import globstyles from "@/lib/globstyles";
import {useEffect, useMemo, useRef, useState} from "react";
import ErrorMessage from "@/lib/components/ErrorMessage";
import GroupCard from "@/lib/components/GroupCard";

export default function Index() {
    const {session} = useSession();
    const router = useRouter();
    const searchInput = useRef<TextInput>(null);

    const {query, filter} = useLocalSearchParams();
    const initFilter = useMemo(() => {
        if (!filter) {
            return "any";
        }
        const value = filter.toString() as SearchFilter;
        if (value === undefined) {
            return "any";
        }
        return value;
    }, [filter]);

    const [searchQueryInput, setSearchQueryInput] = useState(query ? query.toString() : "");
    const [searchQuery, setSearchQuery] = useState(query ? query.toString() : "");
    const [searchFilter, setSearchFilter] = useState<SearchFilter>(initFilter);

    useEffect(() => {
        let queryString = query ? query.toString() : "";
        setSearchQuery(queryString);
        setSearchQueryInput(queryString)
        setSearchFilter(initFilter);
    }, [query, initFilter]);

    const {data, isPending, error} = useQuery<Group[], BackendError>({
        queryKey: ['group', searchFilter, searchQuery, session],
        queryFn: () => {
            const searchParams = new SearchRequest(
                searchQuery || undefined,
                searchFilter || undefined,
            );
            return fetchBackendURLParams("/group", "GET", searchParams.urlParams(), session);
        },
        retry: dataRetryPolicy,
    });

    return (
        <Page scrollable={true}>
            <View style={styles.searchBar}>
                <Pressable focusable={false} onPress={() => router.dismiss()}>
                    <Icon kind={"logo"} size={42} color={Color.Yellow}/>
                </Pressable>
                <TextInput style={[globstyles.formTextInput, styles.searchField]}
                           placeholder={"Søk etter samfunn"}
                           placeholderTextColor={Color.FadedText}
                           value={searchQueryInput}
                           onChangeText={(query) => {
                               setSearchQueryInput(query);
                           }}
                           inputMode={"search"}
                           returnKeyType={"search"}
                           autoFocus={Platform.OS === "web"}
                           focusable={true}
                           keyboardAppearance={"dark"}
                           submitBehavior={"submit"}
                           onSubmitEditing={() => {
                               setSearchQuery(searchQueryInput);
                               searchInput.current?.focus();
                           }}
                />
                {!!session &&
                    <ActionButton
                        icon={"plus"}
                        title={"Ny"}
                        height={42}
                        onPress={() => {
                            router.navigate(`/s/create`);
                        }}

                    />
                }
            </View>

            <View style={{justifyContent: "flex-start", flexDirection: "row", gap: 8}}>
                <ActionButton
                    title={"Alle samfunn"}
                    activated={searchFilter === "any"}
                    onPress={() => setSearchFilter("any")}
                />
                <ActionButton
                    title={"De jeg modererer"}
                    activated={searchFilter === "owned"}
                    onPress={() => setSearchFilter("owned")}
                />
            </View>

            {(data && data.length === 0) && (
                <Message text={`Ingen treff på "${searchQuery}".`}/>
            )}

            {error && <ErrorMessage error={error}/>}
            {isPending &&
                <View style={{marginVertical: 16}}>
                    <ActivityIndicator size="large" color={Color.PrimaryText}/>
                </View>
            }
            {data && data.map(group => <GroupCard key={group.group_id} group={group}></GroupCard>)}

            <Nav>
                <ActionButton title={"Tilbake"} onPress={() => router.dismiss()}/>
            </Nav>
        </Page>
    );
}

export const styles = StyleSheet.create({
    searchBar: {
        flexDirection: "row",
        gap: 8,
        height: 42,
        marginVertical: 4,
    },
    searchField: {
        width: "100%",
        flexShrink: 1,
    },
})